import { useState, useEffect } from 'react';
import { requestPasswordReset, resetPassword } from '../lib/api';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { FunctionComponent } from 'react';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [step, setStep] = useState(1); // 1: Request code, 2: Reset password
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const FaEyeIcon = FaEye as FunctionComponent<{ className?: string }>;
  const FaEyeSlashIcon = FaEyeSlash as FunctionComponent<{ className?: string }>;

  // Extract code from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get('code');
    if (codeFromUrl) {
      setCode(codeFromUrl);
      setStep(2); // Automatically switch to step 2 if code is present
    }
  }, [location.search]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setStep(2);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Please enter the reset code.');
      return;
    }
    if (!password.trim() || !passwordConfirmation.trim()) {
      setError('Please fill in both password fields.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password should be at least 6 characters.');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword({ code, password, passwordConfirmation });
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-background min-h-screen flex items-center justify-center p-4">
      <div className="container max-w-md bg-caption-bg p-8 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold mb-6 text-text text-center">Reset Password</h1>

        {error && (
          <p
            className="text-primary mb-4"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </p>
        )}

        {step === 1 ? (
          <form onSubmit={handleRequestCode} className="space-y-6" noValidate>
            <div>
              <label htmlFor="email" className="block mb-1 text-text font-semibold">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full p-3 border border-border rounded-lg form-input text-text placeholder-text"
                required
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-background p-3 rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
              aria-busy={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-6" noValidate>
            <div>
              <label htmlFor="code" className="block mb-1 text-text font-semibold">
                Reset Code
              </label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter reset code"
                className="w-full p-3 border border-border rounded-lg form-input text-text placeholder-text"
                required
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="block mb-1 text-text font-semibold">
                New Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full p-3 border border-border rounded-lg form-input text-text placeholder-text"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-10 text-text hover:text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlashIcon className="h-5 w-5" /> : <FaEyeIcon className="h-5 w-5" />}
              </button>
            </div>

            <div className="relative">
              <label htmlFor="passwordConfirmation" className="block mb-1 text-text font-semibold">
                Confirm New Password
              </label>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="passwordConfirmation"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="Confirm new password"
                className="w-full p-3 border border-border rounded-lg form-input text-text placeholder-text"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-10 text-text hover:text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
              >
                {showConfirmPassword ? <FaEyeSlashIcon className="h-5 w-5" /> : <FaEyeIcon className="h-5 w-5" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-background p-3 rounded-lg font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
              aria-busy={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-text">
          Back to{' '}
          <Link
            to="/login"
            className="text-primary underline hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Log In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;